import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckSquare, Pencil, Plus, Save, Trash2, Users, X } from 'lucide-react';
import AppShell from '../layouts/AppShell';
import AdminLayout from '../layouts/AdminLayout';
import { API } from '../App';
import { hasPermission, hasAnyPermission } from '../utils/permissionUtils';
import { buildNavigationItems } from '../utils/navigationConfig';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const emptyForm = { name: '', description: '', see_all_messages: false };

const TeamMembersPage = ({ user, onLogout }) => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editorMode, setEditorMode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [memberIds, setMemberIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canManageTeams = useMemo(() => hasPermission(user, 'team:manage'), [user]);
  const canManageTemplates = useMemo(() => hasPermission(user, 'template:manage'), [user]);
  const canManageIntegrations = useMemo(() => hasPermission(user, 'integration:manage'), [user]);
  const canManagePositions = useMemo(() => hasPermission(user, 'position:manage'), [user]);
  const canViewUserRoster = useMemo(
    () => hasAnyPermission(user, ['position:assign', 'position:manage', 'user:manage']),
    [user]
  );
  const canInviteUsers = useMemo(() => hasPermission(user, 'user:invite'), [user]);
  const canViewStats = useMemo(() => hasPermission(user, 'stats:view'), [user]);
  const canModerateComments = useMemo(() => hasPermission(user, 'comment:moderate'), [user]);

  const navigationItems = useMemo(
    () => buildNavigationItems({
      canManageTemplates, canViewUserRoster, canManagePositions, canManageTeams,
      canInviteUsers, canViewStats, canManageIntegrations, canModerateComments,
    }),
    [canManageTemplates, canViewUserRoster, canManagePositions, canManageTeams, canInviteUsers, canViewStats, canManageIntegrations, canModerateComments]
  );

  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === selectedId) || null,
    [teams, selectedId]
  );

  const loadData = useCallback(async () => {
    if (!canManageTeams) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const config = { headers: authHeaders(), timeout: 30000 };
      const [teamResponse, userResponse] = await Promise.all([
        axios.get(`${API}/teams`, config),
        axios.get(`${API}/teams/users`, config),
      ]);
      setTeams(teamResponse.data || []);
      setUsers(userResponse.data || []);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || loadError.message || 'Unable to load teams');
    } finally {
      setLoading(false);
    }
  }, [canManageTeams]);

  useEffect(() => { loadData(); }, [loadData]);

  const closeEditor = () => {
    setEditorMode(null);
    setSelectedId(null);
    setForm(emptyForm);
    setMemberIds([]);
    setError('');
  };

  const openCreate = () => {
    setSelectedId(null);
    setForm(emptyForm);
    setMemberIds([]);
    setError('');
    setEditorMode('create');
  };

  const openEdit = (team) => {
    setSelectedId(team.id);
    setForm({
      name: team.name || '',
      description: team.description || '',
      see_all_messages: Boolean(team.see_all_messages),
    });
    setMemberIds(users.filter((member) => member.team?.id === team.id).map((member) => member.id));
    setError('');
    setEditorMode('edit');
  };

  // A person can only be chosen if unassigned, or already in the team being edited.
  // Members of another team never appear as selectable candidates.
  const selectableUsers = useMemo(() => users.filter((member) => {
    if (!member.team?.id) return true;
    return editorMode === 'edit' && member.team.id === selectedId;
  }), [users, editorMode, selectedId]);

  const assignedElsewhere = useMemo(
    () => users.filter((member) => member.team?.id && member.team.id !== selectedId).length,
    [users, selectedId]
  );

  const toggleMember = (memberId) => {
    setMemberIds((current) => current.includes(memberId)
      ? current.filter((id) => id !== memberId)
      : [...current, memberId]);
  };

  const saveTeam = async () => {
    if (!form.name.trim()) {
      setError('Team name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, name: form.name.trim(), description: form.description.trim() || null };
      const response = editorMode === 'edit'
        ? await axios.put(`${API}/teams/${selectedId}`, payload, { headers: authHeaders() })
        : await axios.post(`${API}/teams`, payload, { headers: authHeaders() });
      const teamId = response.data.id;
      const selectedSet = new Set(memberIds);
      const membershipRequests = selectableUsers
        .filter((member) => {
          const isInSavedTeam = member.team?.id === teamId;
          return selectedSet.has(member.id) !== isInSavedTeam;
        })
        .map((member) => axios.patch(
          `${API}/users/${member.id}/team`,
          { team_id: selectedSet.has(member.id) ? teamId : null },
          { headers: authHeaders() }
        ));
      await Promise.all(membershipRequests);
      await loadData();
      closeEditor();
    } catch (saveError) {
      setError(saveError.response?.data?.detail || saveError.message || 'Unable to save team');
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async () => {
    if (!selectedTeam || !window.confirm(`Delete ${selectedTeam.name}? Members will become unassigned from the team; chats will not be deleted.`)) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.id}`, { headers: authHeaders() });
      await loadData();
      closeEditor();
    } catch (deleteError) {
      setError(deleteError.response?.data?.detail || deleteError.message || 'Unable to delete team');
    }
  };

  const isEditing = editorMode === 'edit';

  return (
    <AppShell user={user} onLogout={onLogout} navItems={navigationItems}>
      <AdminLayout title="Team Members" description="Create teams, then choose who can collaborate inside each team.">
        {!canManageTeams ? (
          <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-6 text-sm text-[var(--tg-text-secondary)]">
            You do not have permission to manage teams.
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
            <section className="min-w-0 self-start rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4 lg:sticky lg:top-0">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--tg-text-muted)]">All teams</p>
                  <h2 className="text-lg font-semibold text-[var(--tg-text-primary)]">{teams.length} {teams.length === 1 ? 'team' : 'teams'}</h2>
                </div>
                <Button size="sm" onClick={openCreate}><Plus className="mr-1 h-4 w-4" />New</Button>
              </div>
              <div className="space-y-2">
                {loading ? <p className="py-8 text-center text-sm text-[var(--tg-text-muted)]">Loading teams…</p> : teams.length ? teams.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => openEdit(team)}
                    className={`w-full rounded-xl border p-3 text-left transition ${selectedId === team.id && isEditing ? 'border-purple-500 bg-purple-500/10' : 'border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] hover:border-purple-500/50'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium text-[var(--tg-text-primary)]">{team.name}</span>
                      <span className="rounded-full bg-[var(--tg-surface)] px-2 py-0.5 text-xs text-[var(--tg-text-muted)]">{team.members?.length || 0}</span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--tg-text-muted)]">{team.see_all_messages ? 'Shared team inbox on' : 'Private assignments'}</p>
                  </button>
                )) : <p className="rounded-xl border border-dashed border-[var(--tg-border-soft)] px-3 py-5 text-center text-sm text-[var(--tg-text-muted)]">No teams yet.</p>}
              </div>
            </section>

            {editorMode ? (
              <section className="min-w-0 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4 sm:p-5">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[var(--tg-text-muted)]">{isEditing ? 'Edit team' : 'Create team'}</p>
                    <h2 className="text-xl font-semibold text-[var(--tg-text-primary)]">{isEditing ? selectedTeam?.name : 'New team'}</h2>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="ghost" size="sm" onClick={closeEditor}><X className="mr-1 h-4 w-4" />Close</Button>
                    {isEditing && <Button variant="outline" size="sm" onClick={deleteTeam}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>}
                    <Button size="sm" onClick={saveTeam} disabled={saving}><Save className="mr-1 h-4 w-4" />{saving ? 'Saving…' : 'Save team'}</Button>
                  </div>
                </div>

                {error && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-1 text-sm text-[var(--tg-text-secondary)]">
                    <span>Team name</span>
                    <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Admissions team" />
                  </label>
                  <label className="space-y-1 text-sm text-[var(--tg-text-secondary)] md:col-span-2">
                    <span>Description</span>
                    <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} className="min-h-20 w-full rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] px-3 py-2 text-[var(--tg-text-primary)] outline-none focus:border-purple-500" placeholder="What this team handles" />
                  </label>
                  <div className="flex flex-col gap-3 rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] p-4 sm:flex-row sm:items-center sm:justify-between md:col-span-2">
                    <div className="pr-4"><p className="font-medium text-[var(--tg-text-primary)]">See all team messages</p><p className="text-xs text-[var(--tg-text-muted)]">Members with View Team Chats can see and reply to chats assigned to teammates.</p></div>
                    <Switch checked={form.see_all_messages} onCheckedChange={(checked) => setForm((current) => ({ ...current, see_all_messages: checked }))} />
                  </div>
                </div>

                <div className="mt-6 border-t border-[var(--tg-border-soft)] pt-5">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><div className="flex items-center gap-2"><Users className="h-4 w-4 text-purple-400" /><h3 className="font-semibold text-[var(--tg-text-primary)]">Choose members</h3></div><span className="text-xs text-[var(--tg-text-muted)]">{memberIds.length} selected</span></div>
                  <p className="mb-3 text-xs text-[var(--tg-text-muted)]">Only people without a team can be added. Members already assigned to another team are excluded.</p>
                  <div className="max-h-[min(52vh,560px)] space-y-2 overflow-y-auto pr-1">
                    {selectableUsers.map((member) => {
                      const selected = memberIds.includes(member.id);
                      return <label key={member.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${selected ? 'border-purple-500/70 bg-purple-500/10' : 'border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] hover:border-purple-500/40'}`}>
                        <input type="checkbox" checked={selected} onChange={() => toggleMember(member.id)} className="h-4 w-4 accent-purple-500" />
                        <div className="min-w-0"><p className="truncate font-medium text-[var(--tg-text-primary)]">{member.name}</p><p className="truncate text-xs text-[var(--tg-text-muted)]">{member.email || member.contact_number || 'No contact'} · {member.position?.name || member.role}</p></div>
                      </label>;
                    })}
                    {!selectableUsers.length && <div className="rounded-xl border border-dashed border-[var(--tg-border-soft)] px-4 py-6 text-center text-sm text-[var(--tg-text-muted)]">No unassigned users are available.</div>}
                  </div>
                  {assignedElsewhere > 0 && <p className="mt-3 text-xs text-[var(--tg-text-muted)]">{assignedElsewhere} {assignedElsewhere === 1 ? 'user is' : 'users are'} already assigned to another team and cannot be added here.</p>}
                </div>
              </section>
            ) : (
              <section className="flex min-h-[360px] min-w-0 flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--tg-border-soft)] bg-[var(--tg-surface)] px-6 text-center">
                <div className="mb-4 rounded-2xl bg-[var(--tg-accent-soft)] p-4 text-purple-300"><CheckSquare className="h-7 w-7" /></div>
                <h2 className="text-xl font-semibold text-[var(--tg-text-primary)]">Select a team to edit</h2>
                <p className="mt-2 max-w-md text-sm text-[var(--tg-text-muted)]">Choose a team from the list to edit it, or create a new team and choose its members before saving.</p>
                <Button className="mt-5" onClick={openCreate}><Plus className="mr-1 h-4 w-4" />Create team</Button>
              </section>
            )}
          </div>
        )}
      </AdminLayout>
    </AppShell>
  );
};

export default TeamMembersPage;
